import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Profiles.css';

const Profiles = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Control de pestañas: 'profiles' o 'modules'
  const [activeTab, setActiveTab] = useState('profiles');

  // Estado para paginación de perfiles
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Modales
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [showModuleFormModal, setShowModuleFormModal] = useState(false);
  
  // Datos temporales de perfiles
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileName, setProfileName] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [selectedProfileModules, setSelectedProfileModules] = useState([]);
  const [currentProfileId, setCurrentProfileId] = useState(null);

  // Datos temporales de módulos
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    name: '',
    label: '',
    icon: '',
    path: '',
    category: 'Principal',
    is_standalone: false,
    sort_order: 0
  });
  const [moduleError, setModuleError] = useState('');

  // Verificar superadmin
  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profilesRes, modulesRes] = await Promise.all([
        axios.get('/api/profiles'),
        axios.get('/api/modules/all')
      ]);
      setProfiles(profilesRes.data);
      setModules(modulesRes.data);
      setError('');
    } catch (err) {
      setError('Error al cargar datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- MÉTODOS DE PERFILES ---
  const handleOpenProfileModal = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setProfileName(profile.name);
      setProfileDescription(profile.description || '');
    } else {
      setEditingProfile(null);
      setProfileName('');
      setProfileDescription('');
    }
    setShowProfileModal(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      if (editingProfile) {
        await axios.put(`/api/profiles/${editingProfile.id}`, {
          name: profileName,
          description: profileDescription
        });
      } else {
        await axios.post('/api/profiles', {
          name: profileName,
          description: profileDescription
        });
      }
      fetchData();
      setShowProfileModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar perfil');
    }
  };

  const handleDeleteProfile = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este perfil?')) return;
    try {
      await axios.delete(`/api/profiles/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar perfil');
    }
  };

  const handleOpenModulesModal = async (profile) => {
    try {
      setCurrentProfileId(profile.id);
      const res = await axios.get(`/api/profiles/${profile.id}/modules`);
      setSelectedProfileModules(res.data.map(m => m.id));
      setShowModulesModal(true);
    } catch (err) {
      setError('Error al obtener módulos del perfil');
    }
  };

  const handleToggleModule = (moduleId) => {
    setSelectedProfileModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId) 
        : [...prev, moduleId]
    );
  };

  const handleSaveModules = async () => {
    try {
      await axios.post('/api/profiles/assign-modules', {
        profileId: currentProfileId,
        moduleIds: selectedProfileModules
      });
      setShowModulesModal(false);
      alert('Módulos asignados correctamente');
    } catch (err) {
      setError('Error al asignar módulos');
    }
  };

  // --- MÉTODOS DE MÓDULOS ---
  const handleOpenModuleModal = (module = null) => {
    setModuleError('');
    if (module) {
      setEditingModule(module);
      setModuleForm({
        name: module.name,
        label: module.label,
        icon: module.icon || '',
        path: module.path,
        category: module.category || 'Principal',
        is_standalone: !!module.is_standalone,
        sort_order: module.sort_order || 0
      });
    } else {
      setEditingModule(null);
      setModuleForm({
        name: '',
        label: '',
        icon: '',
        path: '',
        category: 'Principal',
        is_standalone: false,
        sort_order: 0
      });
    }
    setShowModuleFormModal(true);
  };

  const handleSaveModule = async (e) => {
    e.preventDefault();
    setModuleError('');
    try {
      if (editingModule) {
        await axios.put(`/api/modules/${editingModule.id}`, moduleForm);
      } else {
        await axios.post('/api/modules', moduleForm);
      }
      fetchData();
      setShowModuleFormModal(false);
      alert(editingModule ? 'Módulo actualizado correctamente. Por favor recarga el sitio para actualizar el menú.' : 'Módulo creado correctamente.');
    } catch (err) {
      setModuleError(err.response?.data?.message || 'Error al guardar el módulo');
    }
  };

  const handleDeleteModule = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este módulo? Se borrarán sus accesos asociados en todos los perfiles de usuario.')) return;
    try {
      await axios.delete(`/api/modules/${id}`);
      fetchData();
      alert('Módulo eliminado. Por favor recarga el sitio para actualizar el menú.');
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar módulo');
    }
  };

  // Paginación perfiles
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = profiles.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🛡️ Gestión de Perfiles y Menú" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          {/* Navegación por pestañas */}
          <div className="tabs-container" style={{ display: 'flex', gap: '20px', marginBottom: '25px', borderBottom: '1px solid var(--card-border)' }}>
            <button 
              onClick={() => setActiveTab('profiles')}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: 'none',
                color: activeTab === 'profiles' ? 'var(--primary-color)' : 'var(--text-secondary)',
                fontWeight: 'bold',
                cursor: 'pointer',
                borderBottom: activeTab === 'profiles' ? '3px solid var(--primary-color)' : '3px solid transparent',
                transition: 'all 0.3s ease'
              }}
            >
              🛡️ Perfiles de Usuario
            </button>
            <button 
              onClick={() => setActiveTab('modules')}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: 'none',
                color: activeTab === 'modules' ? 'var(--primary-color)' : 'var(--text-secondary)',
                fontWeight: 'bold',
                cursor: 'pointer',
                borderBottom: activeTab === 'modules' ? '3px solid var(--primary-color)' : '3px solid transparent',
                transition: 'all 0.3s ease'
              }}
            >
              ⚙️ Módulos y Menú Lateral
            </button>
          </div>

          {/* VISTA 1: PERFILES */}
          {activeTab === 'profiles' && (
            <>
              <div className="page-header">
                <div className="header-title">
                  <h2>Perfiles del Sistema</h2>
                  <p className="subtitle">Configura qué módulos puede ver cada tipo de usuario</p>
                </div>
                <button onClick={() => handleOpenProfileModal()} className="btn-primary">
                  ➕ Nuevo Perfil
                </button>
              </div>

              {loading ? (
                <p className="loading">Cargando perfiles...</p>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Descripción</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map(p => (
                        <tr key={p.id}>
                          <td className="user-name">{p.name}</td>
                          <td>{p.description}</td>
                          <td className="actions">
                            <button 
                              onClick={() => handleOpenModulesModal(p)} 
                              className="btn-action btn-modules"
                              title="Asignar Módulos"
                            >
                              📦 Módulos
                            </button>
                            <button 
                              onClick={() => handleOpenProfileModal(p)} 
                              className="btn-action btn-edit"
                              title="Editar Perfil"
                            >
                              ✏️ Editar
                            </button>
                            <button 
                              onClick={() => handleDeleteProfile(p.id)} 
                              className="btn-action btn-delete"
                              title="Eliminar Perfil"
                            >
                              🗑️ Borrar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <Pagination 
                    currentPage={currentPage}
                    totalItems={profiles.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              )}
            </>
          )}

          {/* VISTA 2: MÓDULOS */}
          {activeTab === 'modules' && (
            <>
              <div className="page-header">
                <div className="header-title">
                  <h2>Módulos y Secciones</h2>
                  <p className="subtitle">Personaliza los accesos, nombres y secciones del menú lateral</p>
                </div>
                <button onClick={() => handleOpenModuleModal()} className="btn-primary">
                  ➕ Nuevo Módulo
                </button>
              </div>

              {loading ? (
                <p className="loading">Cargando módulos...</p>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Icono</th>
                        <th>Nombre Interno</th>
                        <th>Etiqueta en Menú</th>
                        <th>Ruta (Path)</th>
                        <th>Sección (Categoría)</th>
                        <th style={{ textAlign: 'center' }}>Por Fuera</th>
                        <th style={{ textAlign: 'center' }}>Orden</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(m => (
                        <tr key={m.id}>
                          <td style={{ fontSize: '20px', textAlign: 'center', width: '70px' }}>{m.icon || '—'}</td>
                          <td><code>{m.name}</code></td>
                          <td><strong>{m.label}</strong></td>
                          <td><code>{m.path}</code></td>
                          <td>
                            <span style={{ 
                              padding: '4px 8px', 
                              background: 'var(--success-soft, #f0fdf4)', 
                              color: 'var(--success-color, #16a34a)', 
                              borderRadius: '6px', 
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {m.category || 'Otros'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ 
                              padding: '4px 8px', 
                              background: m.is_standalone ? 'var(--warning-soft, #fffbeb)' : 'var(--text-secondary-soft, #f1f5f9)', 
                              color: m.is_standalone ? 'var(--warning-color, #d97706)' : 'var(--text-secondary, #64748b)', 
                              borderRadius: '6px', 
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {m.is_standalone ? 'Sí' : 'No'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <code>{m.sort_order || 0}</code>
                          </td>
                          <td className="actions">
                            <button 
                              onClick={() => handleOpenModuleModal(m)} 
                              className="btn-action btn-edit"
                              title="Editar Módulo"
                            >
                              ✏️ Editar
                            </button>
                            <button 
                              onClick={() => handleDeleteModule(m.id)} 
                              className="btn-action btn-delete"
                              title="Eliminar Módulo"
                            >
                              🗑️ Borrar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {modules.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                      No hay ningún módulo registrado en el sistema.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Modal de Perfil */}
      {showProfileModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingProfile ? 'Editar Perfil' : 'Nuevo Perfil'}</h3>
            <form onSubmit={handleSaveProfile}>
              <div className="form-group">
                <label>Nombre del Perfil:</label>
                <input 
                  type="text" 
                  value={profileName} 
                  onChange={(e) => setProfileName(e.target.value)} 
                  required 
                  placeholder="Ej: Operador, Auditor..."
                />
              </div>
              <div className="form-group">
                <label>Descripción:</label>
                <textarea 
                  value={profileDescription} 
                  onChange={(e) => setProfileDescription(e.target.value)}
                  placeholder="Descripción de las funciones del perfil"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowProfileModal(false)} className="btn btn-cancel">Cancelar</button>
                <button type="submit" className="btn btn-save">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Asignar Módulos */}
      {showModulesModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <h3>Asignar Módulos al Perfil</h3>
            <p className="modal-subtitle">Selecciona los módulos a los que este perfil tendrá acceso</p>
            
            <div className="modules-grid">
              {Object.entries(
                modules.reduce((acc, m) => {
                  const cat = m.category || 'Otros';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(m);
                  return acc;
                  //eslint-disable-next-line react-hooks/exhaustive-deps
                }, {})
              ).map(([category, catModules]) => (
                <div key={category} className="module-category-box">
                  <h4>{category}</h4>
                  <div className="category-modules">
                    {catModules.map(m => (
                      <label key={m.id} className="module-checkbox-item">
                        <input 
                          type="checkbox" 
                          checked={selectedProfileModules.includes(m.id)}
                          onChange={() => handleToggleModule(m.id)}
                        />
                        <span className="module-icon">{m.icon}</span>
                        <span className="module-label">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowModulesModal(false)} className="btn btn-cancel">Cancelar</button>
              <button onClick={handleSaveModules} className="btn btn-save">Guardar Asignaciones</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Formulario de Módulos (Menú) */}
      {showModuleFormModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingModule ? '📝 Editar Módulo de Menú' : '➕ Nuevo Módulo de Menú'}</h3>
            <p className="modal-subtitle">Configura los datos del item del menú lateral</p>
            {moduleError && <div className="error-banner" style={{ marginBottom: '15px' }}>{moduleError}</div>}
            
            <form onSubmit={handleSaveModule}>
              <div className="form-group">
                <label>Nombre Único (ej: `mis-tramites`):</label>
                <input 
                  type="text" 
                  value={moduleForm.name} 
                  onChange={(e) => setModuleForm({ ...moduleForm, name: e.target.value })} 
                  required 
                  disabled={!!editingModule}
                  placeholder="ej: mis-tramites, email-monitor..."
                />
              </div>
              <div className="form-group">
                <label>Etiqueta en Menú (ej: `Mis Trámites`):</label>
                <input 
                  type="text" 
                  value={moduleForm.label} 
                  onChange={(e) => setModuleForm({ ...moduleForm, label: e.target.value })} 
                  required 
                  placeholder="Nombre a mostrar en el menú..."
                />
              </div>
              <div className="form-group">
                <label>Emoji/Icono (ej: `📁`, `📊`, `📧`):</label>
                <input 
                  type="text" 
                  value={moduleForm.icon} 
                  onChange={(e) => setModuleForm({ ...moduleForm, icon: e.target.value })} 
                  placeholder="Emoji o icono..."
                />
              </div>
              <div className="form-group">
                <label>Ruta / URL Path (ej: `/mis-tramites`):</label>
                <input 
                  type="text" 
                  value={moduleForm.path} 
                  onChange={(e) => setModuleForm({ ...moduleForm, path: e.target.value })} 
                  required 
                  placeholder="/ruta-ejemplo..."
                />
              </div>
              <div className="form-group">
                <label>Sección / Categoría (ej: `Trámites`, `Administración`):</label>
                <input 
                  type="text" 
                  value={moduleForm.category} 
                  onChange={(e) => setModuleForm({ ...moduleForm, category: e.target.value })} 
                  placeholder="Ej: Principal, Operaciones, Administración..."
                  disabled={moduleForm.is_standalone}
                />
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px', fontSize: '11px' }}>
                  {moduleForm.is_standalone 
                    ? 'No se requiere categoría para módulos independientes (por fuera).' 
                    : 'Agrupa los accesos en el menú lateral bajo este nombre colapsable.'}
                </small>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '15px 0' }}>
                <input 
                  type="checkbox" 
                  id="is_standalone"
                  checked={moduleForm.is_standalone} 
                  onChange={(e) => setModuleForm({ ...moduleForm, is_standalone: e.target.checked })}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <label htmlFor="is_standalone" style={{ marginBottom: 0, cursor: 'pointer', fontWeight: 'normal' }}>
                  Mostrar por fuera (Standalone - sin sección colapsable)
                </label>
              </div>
              <div className="form-group">
                <label>Orden de Visualización:</label>
                <input 
                  type="number" 
                  value={moduleForm.sort_order} 
                  onChange={(e) => setModuleForm({ ...moduleForm, sort_order: parseInt(e.target.value) || 0 })} 
                  required
                  placeholder="0"
                />
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px', fontSize: '11px' }}>
                  Define la prioridad en el menú lateral. Los números menores aparecen primero. Las secciones se ordenan según el menor orden de sus módulos.
                </small>
              </div>
              <div className="modal-actions" style={{ marginTop: '25px' }}>
                <button type="button" onClick={() => setShowModuleFormModal(false)} className="btn btn-cancel">Cancelar</button>
                <button type="submit" className="btn btn-save" style={{ background: 'var(--primary-color)' }}>Guardar Módulo</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profiles;
